import { createSelector } from "@reduxjs/toolkit";
import {
	getFacilityCardProps,
	getFacilityWithUnits,
} from "store/selectors/entities/facility";
import { isEmpty } from "lodash";
import buildSearchLink from "utils/search/buildSearchLink";
import { getUnitsById, getUnitCardProps, getUniqueNearbyUnits, getNearbyUnitCardProps } from "./entities/units";

export const MISSING_FACILITY_IMAGE_URL = "https://images.sparefoot.com/sdc-missing-facility.jpg";

export const selectFacilityPageMeta = ({ app, facilities }) => ({
	noIndex: facilities.currentFacility.noIndex,
	title: `${facilities.currentFacility.title  } - Storage.com`, 
	canonical: app.baseUrl + facilities.currentFacility.canonical,
	description: `${facilities.currentFacility.title  } - Storage.com`,
});

export const selectCurrentFacility = ({ facilities }) => facilities.byId[facilities.currentFacility.id];

export const selectStaticMapUrl = ({ facilities }) => facilities.currentFacility.staticMapUrl;

export const selectFacilityPageHeader = createSelector(
	selectCurrentFacility,
	({ name, address, cityUrl, location, reviews, stateUrl, url, phone, hasOnlineMoveins, facilityDistanceCity }) => ({
		name,
		address,
		cityUrl,
		location,
		reviews,
		stateUrl,
		url,
		phone,
		hasOnlineMoveins,
		facilityDistanceCity
	})
);

export const selectFacilityImages = createSelector(
	selectCurrentFacility,
	({ images }) => {
		if (!images || images.length === 0) {
			// Fallback image for facilities with no images
			return [MISSING_FACILITY_IMAGE_URL];
		}
		return images.map(
			(i) => `https://images.storage.com/large-compress/${i}`
		);
	}
);

export const selectFacilityLocation = createSelector(
	selectCurrentFacility,
	({ address, name, reviews, url }) => ({
		address,
		name,
		reviews,
		url
	})
);

export const selectFacilityHours = createSelector(
	selectCurrentFacility,
	({ hours = {} }) => ({
		access: hours.access,
		office: hours.office
	})
);

// Returns the facility fees and total fees as props
export const selectFacilityFees = createSelector(
	selectCurrentFacility,
	({ adminFee, lockFee, totalFees, showFeeWarning }) => ({
		adminFee,
		lockFee,
		totalFees,
		showFeeWarning
	})
);

// Formats a review object for a Review Card
export const getFacilityPageReviewCardProps = (review) => {
	const { id, title, nickname, timestamp, rating, message } = review;
	const dateFormatOptions = {
		month: "long",
		day: "numeric",
		year: "numeric"
	};
	const dateNum = new Date(Date.UTC(...timestamp.split(/[- :]/))).getTime();
	return {
		id,
		title,
		nickname,
		date: new Date(timestamp).toLocaleDateString("en", dateFormatOptions),
		dateNum,
		rating,
		message
	};
};

// Returns all the reviews for a facility as Review Card props
export const selectCurrentFacilityReviewCards = createSelector(
	selectCurrentFacility,
	({ reviews }) => reviews,
	(facility, reviews) =>
		(facility.userReviews || [])
			.map((id) => reviews.byId[id])
			.map(getFacilityPageReviewCardProps)
);

// Returns the props for the Facility Reviews section
export const selectFacilityReviewsSection = createSelector(
	selectCurrentFacility,
	selectCurrentFacilityReviewCards,
	(facility, reviews) => ({
		totalReviews: facility.reviews?.number,
		averageRating: facility.reviews?.average,
		reviews
	})
);

export const selectNearbyFacilities = createSelector(
	selectCurrentFacility,
	({ facilities }) => facilities.byId,
	({ units }) => units.byId,
	({ nearbyFacilities }, facilitiesById, unitsById) => {
		if (!nearbyFacilities || nearbyFacilities.length === 0) {
			return [];
		}
		return nearbyFacilities
			.slice(0, 3)
			.map((i) => getFacilityWithUnits(i, facilitiesById, unitsById))
			.map((f) => getFacilityCardProps(f))
			.filter((f) => f);
	}
);

export const selectNearbyFacilitiesUnits = createSelector(
	selectCurrentFacility,
	({ facilities }) => facilities.byId,
	({ units }) => units.byId,
	({ nearbyFacilities }, facilitiesById, unitsById) => {
		if (!nearbyFacilities || nearbyFacilities.length === 0) {
			return [];
		}
		// returns the facility.unit objects from each of the nearby facilities.
		const nearbyFacilityUnits = nearbyFacilities.map(nearbyFacilityId => facilitiesById[nearbyFacilityId].units);
		const nearbyUnits = getUniqueNearbyUnits(nearbyFacilityUnits, unitsById)
		return nearbyUnits.map((unit) => getNearbyUnitCardProps(unit, facilitiesById))
	}
);

const featuresExpectedOrder = [
	"billing",
	"access",
	"administration",
	"vehicle",
	"moving",
	"security",
	"insurance",
	"discounts",
	"premium",
	"other"
];

export const sortFeatures = (groupedAmenities) => {
	const sortedFeatures = {};
	featuresExpectedOrder.forEach((feature) => {
		if (!isEmpty(groupedAmenities) && groupedAmenities[feature]) {
			sortedFeatures[feature] = groupedAmenities[feature];
		}
	});
	return sortedFeatures;
};

export const selectFacilityUnits = createSelector(
	selectCurrentFacility,
	({ units }) => units.byId,
	(currentFacility, unitsById) => {
		const { storageUnits = [], vehicleUnits = [] } = currentFacility.units || {};
		return {
			storageUnits: getUnitsById(storageUnits, unitsById).map(
				getUnitCardProps
			),
			vehicleUnits: getUnitsById(vehicleUnits, unitsById).map(
				getUnitCardProps
			)
		};
	}
);

export const selectNearbyFacilitiesUrl = createSelector(
	selectCurrentFacility,
	(facility) => {
		const { location, cityUrl } = facility;
		const { city, state } = location;
		const url = buildSearchLink({
			city,
			state,
			location: `${city}, ${state}`
		})
		return cityUrl || url;
	}
);

export const selectFacilityBannerProps = createSelector(
	selectCurrentFacility,
	(facility) => ({
		hasOnlineMoveins: facility.hasOnlineMoveins,
		discountItems: facility.groupedAmenities?.discounts?.items || null
	})
);

export const facilityPageSelector = createSelector(
	selectFacilityPageMeta,
	selectCurrentFacility,
	selectFacilityPageHeader,
	selectFacilityImages,
	selectFacilityLocation,
	selectFacilityHours,
	selectFacilityFees,
	selectFacilityReviewsSection,
	selectNearbyFacilities,
	selectFacilityUnits,
	selectNearbyFacilitiesUnits,
	selectNearbyFacilitiesUrl,
	selectFacilityBannerProps,
	selectStaticMapUrl,
	(
		facilityMeta,
		currentFacility,
		facilityHeader,
		facilityImages,
		facilityLocation,
		facilityHours,
		facilityFees,
		facilityReviewsSection,
		nearbyFacilities,
		facilityUnits,
		nearbyFacilitiesUnits,
		nearbyFacilitiesUrl,
		facilityBannerProps,
		staticMapUrl
	) => ({
		facilityId: currentFacility.id,
		promotions: currentFacility.promotions,
		meta: facilityMeta,
		descriptionProps: { description: currentFacility.description },
		headerProps: facilityHeader,
		imagesProps: { images: facilityImages },
		locationModuleProps: facilityLocation,
		hoursProps: facilityHours,
		feesProps: facilityFees,
		reviewsProps: facilityReviewsSection,
		nearbyFacilitiesProps: {
			facilities: nearbyFacilities
		},
		featuresAndAmenitiesProps: {
			amenities: sortFeatures(currentFacility.groupedAmenities),
			adminFee: currentFacility.adminFee
		},
		facilityUnits,
		hasUnits:
			facilityUnits.storageUnits.length > 0 ||
			facilityUnits.vehicleUnits.length > 0,
		nearbyFacilitiesUnits,
		closures: currentFacility.closures,
		nearbyFacilitiesUrl,
		location: currentFacility.location,
		staticMapUrl,
		facilityBannerProps,
		featuresDataByCategory: currentFacility.featuresDataByCategory,
		currentFacility,
	})
);

export const selectFacilityPageMetaV2 = ({ app, facilities }) => {
	const rootUrl = app.baseUrl || "https://www.storage.com";
	const {canonical} = facilities.currentFacility;
	const facilityTitle = facilities.currentFacility.title?.replace(/\s*:?\s*Lowest Rates/i, "").trim() || facilities.currentFacility.title;
	return {
		noIndex: facilities.currentFacility.noIndex,
		title: `${facilityTitle} | Storage.com`,
		canonical: canonical ? rootUrl + canonical : null,
		description: `Reserve a storage unit at ${facilityTitle} quickly and safely. See current pricing, read reviews, and book online today.`,
	};
};

export const selectFacilitySchemaProps = createSelector(
	selectCurrentFacility,
	({ url, name, location, reviews, hours, featuresDataByCategory }) => ({
		url,
		name,
		location,
		reviews,
		hours,
		featuresDataByCategory
	})
);

export const facilityPageSelectorV2 = createSelector(
	selectFacilityPageMetaV2,
	selectCurrentFacility,
	selectFacilityPageHeader,
	selectFacilityImages,
	selectFacilityLocation,
	selectFacilityHours,
	selectFacilityFees,
	selectFacilityReviewsSection,
	selectNearbyFacilities,
	selectFacilityUnits,
	selectNearbyFacilitiesUnits,
	selectNearbyFacilitiesUrl,
	selectFacilityBannerProps,
	selectFacilitySchemaProps,
	(
		facilityMeta,
		currentFacility,
		facilityHeader,
		facilityImages,
		facilityLocation,
		facilityHours,
		facilityFees,
		facilityReviewsSection,
		nearbyFacilities,
		facilityUnits,
		nearbyFacilitiesUnits,
		nearbyFacilitiesUrl,
		facilityBannerProps,
		facilitySchemaProps
	) => ({
		facilityId: currentFacility.id,
		promotions: currentFacility.promotions,
		meta: facilityMeta,
		descriptionProps: { description: currentFacility.description },
		headerProps: facilityHeader,
		imagesProps: { images: facilityImages },
		locationModuleProps: facilityLocation,
		hoursProps: facilityHours,
		feesProps: facilityFees,
		reviewsProps: facilityReviewsSection,
		nearbyFacilitiesProps: {
			facilities: nearbyFacilities
		},
		featuresAndAmenitiesProps: {
			amenities: sortFeatures(currentFacility.groupedAmenities),
			adminFee: currentFacility.adminFee
		},
		facilityUnits,
		hasUnits:
			facilityUnits.storageUnits.length > 0 ||
			facilityUnits.vehicleUnits.length > 0,
		nearbyFacilitiesUnits,
		closures: currentFacility.closures,
		nearbyFacilitiesUrl,
		location: currentFacility.location,
		staticMapUrl: currentFacility.staticMapUrl,
		facilityBannerProps,
		featuresDataByCategory: currentFacility.featuresDataByCategory,
		facilitySchemaProps,
	})
);

export default facilityPageSelector;
